import * as Dial from '@studio/design-system/dial';
import { Label } from '@studio/design-system/label';
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Column, Exhibit } from './styled.jsx';

const Shutter = (): ReactElement => {
  const [value, setValue] = useState(2);

  const props = useMemo(
    () => ({
      formatter: (µ: Dial.Item): string => `1/${µ as number}`,
      items: [30, 60, 125, 250, 500, 750, 1000],
      onChange: (_: unknown, index: number): void => {
        setValue(index);
      }
    }),
    []
  );

  return (
    <Dial.Container label='Shutter speed'>
      <Dial.Root selectedItem={props.items[value]} {...props} />
    </Dial.Container>
  );
};

const Iris = (): ReactElement => {
  const [value, setValue] = useState(4);

  const props = useMemo(
    () => ({
      formatter: (µ: Dial.Item): string => `F${µ as number}`,
      items: [1.4, 2.8, 4, 5.6, 8, 11, 16, 22],
      onChange: (_: unknown, index: number): void => {
        setValue(index);
      }
    }),
    []
  );

  return (
    <Dial.Container label='Iris'>
      <Dial.Root selectedItem={props.items[value]} {...props} />
    </Dial.Container>
  );
};

const Exposure = (): ReactElement => {
  const [value, setValue] = useState(3);

  const props = useMemo(
    () => ({
      items: [
        {
          label: '-1',
          value: -1
        },
        {
          label: '-2/3',
          value: -0.6
        },
        {
          label: '-1/3',
          value: -0.3
        },
        0,
        {
          label: '+1/3',
          value: 0.3
        },
        {
          label: '+2/3',
          value: 0.6
        },
        {
          label: '+1',
          value: 1
        }
      ],
      onChange: (_: unknown, index: number): void => {
        setValue(index);
      }
    }),
    []
  );

  return (
    <Dial.Container label='Exposure compensation'>
      <Dial.Root selectedItem={props.items[value]} {...props} />
    </Dial.Container>
  );
};

const Rating = (): ReactElement => {
  const [value, setValue] = useState(1);

  const props = useMemo(
    () => ({
      formatter: (µ: Dial.Item): string => '\u2605'.repeat(µ as number),
      items: [1, 2, 3, 4],
      onChange: (_: unknown, index: number): void => {
        setValue(index);
      }
    }),
    []
  );

  return (
    <Dial.Container label={<Label>Rating</Label>}>
      <Dial.Root selectedItem={props.items[value]} {...props} />
    </Dial.Container>
  );
};

/** `<Dials>` exhibit. */
export const Dials = (): ReactElement => (
  <Exhibit title='Dials'>
    <Column>
      <Shutter />
      <Iris />
      <Exposure />
      <Rating />
      <Dial.Container label={<Label>Disabled</Label>}>
        <Dial.Root
          disabled
          formatter={(µ): string => '$'.repeat(µ as number)}
          items={[1, 2, 3, 4]}
          selectedItem={2}
        />
      </Dial.Container>
    </Column>
  </Exhibit>
);
